import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { HelperText, Text, useTheme } from 'react-native-paper';
import { mustBeValidDate, mustBeValidDay, mustBeValidMonth, mustBeValidYear, mustPastOrPresentYear, } from '@luminate/luminate-ts-sdk';
import { useValidated } from '../hooks';
import { ValidatedInput } from './ValidatedInput';
export const ValidatedDateInput = forwardRef((props, ref) => {
    const theme = useTheme();
    //eslint-disable-next-line
    // @ts-ignore
    const monthInputRef = useRef(null);
    //eslint-disable-next-line
    // @ts-ignore
    const dayInputRef = useRef(null);
    //eslint-disable-next-line
    // @ts-ignore
    const yearInputRef = useRef(null);
    const [monthErrors, onMonthValidated, isMonthValid] = useValidated();
    const [monthFocus, setMonthFocus] = useState(false);
    const [dayErrors, onDayValidated, isDayValid] = useValidated();
    const [dayFocus, setDayFocus] = useState(false);
    const [yearErrors, onYearValidated, isYearValid] = useValidated();
    const [yearFocus, setYearFocus] = useState(false);
    const validateAllInputs = () => {
        const isMontValid = monthInputRef.current?.isValid() ?? true;
        const isDayValid = dayInputRef.current?.isValid() ?? true;
        const isYearValid = yearInputRef.current?.isValid() ?? true;
        return isMontValid && isDayValid && isYearValid;
    };
    useImperativeHandle(ref, () => ({
        isValid: validateAllInputs,
    }));
    const hasError = useMemo(() => {
        return props.errors && props.errors.length > 0;
    }, [props.errors]);
    const [hasFocus, setHasFocus] = useState(false);
    const month = useMemo(() => {
        return props.value?.split('/')[0] || '';
    }, [props.value]);
    const day = useMemo(() => {
        return props.value?.split('/')[1] || '';
    }, [props.value]);
    const year = useMemo(() => {
        return props.value?.split('/')[2] || '';
    }, [props.value]);
    const compileDateText = (month, day, year) => {
        const monthDisplay = `${month || ''}`;
        const dayDisplay = day ? `/${day}` : '';
        const yearDisplay = year ? `/${year}` : '';
        return `${monthDisplay}${dayDisplay}${yearDisplay}`;
    };
    const onChangeText = (changedMonth = month, changedDay = day, changedYear = year) => {
        if (props.onChangeText) {
            props.onChangeText(compileDateText(changedMonth, changedDay, changedYear));
        }
    };
    const allRules = useMemo(() => {
        return new Array(...(props.rules || []), ...[mustBeValidDate]);
    }, [props.rules]);
    useEffect(() => {
        setHasFocus(monthFocus || dayFocus || yearFocus);
    }, [monthFocus, dayFocus, yearFocus]);
    useEffect(() => {
        const dobValidationErrors = new Array();
        if (!isMonthValid) {
            dobValidationErrors.push(...monthErrors);
        }
        if (!isDayValid) {
            dobValidationErrors.push(...dayErrors);
        }
        if (!isYearValid) {
            dobValidationErrors.push(...yearErrors);
        }
        if (month && day && year) {
            const fullDate = compileDateText(month, day, year);
            allRules.forEach((validator) => {
                const error = validator(fullDate);
                if (error) {
                    dobValidationErrors.push(error);
                }
            });
        }
        if (props.onValidate) {
            props.onValidate(dobValidationErrors);
        }
    }, [monthErrors, dayErrors, yearErrors]);
    const focusHandler = (value, 
    //@ts-ignore
    inputRef) => {
        if (value?.length === 2) {
            inputRef.current?.focus();
        }
    };
    const onMonthChangeText = (value) => {
        onChangeText(value);
        focusHandler(value, dayInputRef);
    };
    const onDayChangeText = (value) => {
        onChangeText(month, value);
        focusHandler(value, yearInputRef);
    };
    const onYearChangeText = (value) => {
        onChangeText(month, day, value);
    };
    const useBackspaceHandler = (value, inputRef) => {
        return (event) => {
            if (event.nativeEvent.key === 'Backspace' && value?.length === 0) {
                inputRef.current?.focus();
            }
        };
    };
    const onDayKeyPress = useBackspaceHandler(day, monthInputRef);
    const onYearKeyPress = useBackspaceHandler(year, dayInputRef);
    const getHighlightColor = () => {
        return hasError ? theme.colors.error : hasFocus ? theme.colors.primary : theme.colors.outline;
    };
    const getHighlightWidth = () => {
        return hasError || hasFocus ? 2 : 1;
    };
    const onFocus = (event) => {
        if (props.onFocus) {
            props.onFocus(event);
        }
    };
    const onBlur = (event) => {
        if (props.onBlur) {
            props.onBlur(event);
        }
    };
    const onMonthFocus = (event) => {
        setMonthFocus(true);
        onFocus(event);
    };
    const onDayFocus = (event) => {
        setDayFocus(true);
        onFocus(event);
    };
    const onYearFocus = (event) => {
        setYearFocus(true);
        onFocus(event);
    };
    const onMonthBlur = (event) => {
        setMonthFocus(false);
        onBlur(event);
    };
    const onDayBlur = (event) => {
        setDayFocus(false);
        onBlur(event);
    };
    const onYearBlur = (event) => {
        setYearFocus(false);
        onBlur(event);
    };
    const { testID, ...rest } = props;
    return (_jsxs(View, { children: [_jsxs(View, { testID: testID, style: {
                    flex: 1,
                    marginTop: 5,
                    borderStyle: 'solid',
                    borderBottomWidth: 1,
                    borderBottomColor: getHighlightColor(),
                    borderLeftWidth: getHighlightWidth(),
                    borderLeftColor: getHighlightColor(),
                    borderTopWidth: getHighlightWidth(),
                    borderTopColor: getHighlightColor(),
                    borderRightWidth: getHighlightWidth(),
                    borderRightColor: getHighlightColor(),
                    borderRadius: 3,
                }, children: [_jsx(Text, { style: {
                            position: 'absolute',
                            paddingHorizontal: 6,
                            top: -9,
                            left: 6,
                            zIndex: 1,
                            backgroundColor: theme.colors.background,
                            color: getHighlightColor(),
                        }, variant: 'bodySmall', children: props.label }), _jsxs(View, { style: {
                            flex: 1,
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }, children: [_jsx(ValidatedInput, { testID: `${testID}-month`, ...rest, ref: monthInputRef, mode: 'flat', value: month, style: { flex: 1, backgroundColor: theme.colors.background }, label: 'Month', placeholder: 'MM', keyboardType: 'decimal-pad', maxLength: 2, rules: [mustBeValidMonth], errors: monthErrors, hideErrors: true, onValidate: onMonthValidated, onChangeText: onMonthChangeText, onFocus: onMonthFocus, onBlur: onMonthBlur }), _jsx(ValidatedInput, { testID: `${testID}-day`, ...rest, ref: dayInputRef, mode: 'flat', value: day, style: { flex: 1, backgroundColor: theme.colors.background }, label: 'Day', placeholder: 'DD', keyboardType: 'decimal-pad', maxLength: 2, rules: [mustBeValidDay], errors: dayErrors, hideErrors: true, onValidate: onDayValidated, onChangeText: onDayChangeText, onKeyPress: onDayKeyPress, onFocus: onDayFocus, onBlur: onDayBlur }), _jsx(ValidatedInput, { testID: `${testID}-year`, ...rest, ref: yearInputRef, mode: 'flat', value: year, style: { flex: 1, backgroundColor: theme.colors.background }, label: 'Year', placeholder: 'YYYY', keyboardType: 'decimal-pad', maxLength: 4, rules: [mustBeValidYear, mustPastOrPresentYear], errors: yearErrors, hideErrors: true, onValidate: onYearValidated, onKeyPress: onYearKeyPress, onChangeText: onYearChangeText, onFocus: onYearFocus, onBlur: onYearBlur })] })] }), props.errors?.map((error, index) => (_jsx(HelperText, { testID: `${props.testID}_errors`, type: 'error', style: props.errorsStyle, children: error }, `error_${index}`)))] }));
});
